import useUrlState from '@ahooksjs/use-url-state';
import { ProFormInstance } from '@ant-design/pro-form';
import { CharacterInputValue } from '@tencent/tds-ui';
import { useLocalStorageState } from 'ahooks';
import { message, Space } from 'antd';
import { isEmpty } from 'lodash-es';
import React, { FC, useContext, useRef, useState } from 'react';
import * as uuid from 'uuid';
import  StringTooltipIcon from '@/assets/images/icons/string-tooltip-icon.react.svg?react';
import { useAppConfig } from '@/config/context';
import { PAGE_ID } from '@/infrastructure/reporter/datong/id';
import { isDataLabelingPlatform } from '@/infrastructure/utils/issue';
import {
  CHARACTER_MATCH_ARRAY_OPTIONS,
  CHARACTER_MATCH_OPTIONS, INFERRED_LABELS_OPTIONS,
  MatchMode,
  NUMBER_MATCH_OPTIONS,
  STACK_CHARACTER_MATCH_OPTIONS,
  VERSION_MATCH_OPTIONS,
} from '@/presentation/components/form/standards/types';
import { FilterEntry } from '@/presentation/components/form/types';
import { tokenizeValue } from '@/presentation/components/form/utils';
import { ABTEST_TIPS, BUNDLE_TIPS_CASE, hintChildren, hintText } from '@/presentation/components/hint-text/hintText';
import {
  DATA_LABELING_TIPS,
  launchIdText,
  numberFilterTips,
  stringFilterTips,
  versionFilterTips,
} from '@/presentation/components/issue-list/components/tips';
import { SplitModal } from '@/presentation/components/split-modal';
import { CompareParams, TdsFilterType, TdsForm, TdsFormItem, TdsFormItemType } from '@/presentation/components/tds-form';
import { DEFAULT_EVENT_TIME_RANGE } from '@/presentation/components/tds-form/config';
import { TdsCustomFieldsValue } from '@/presentation/components/tds-form/tds-custom-fields';
import { EVENT_TIME_OPTIONS, ISSUE_TIME_TYPE } from '@/presentation/components/tds-form/tds-date-cascader/config';
import { customFields2Filter, dateCascaderConverter, dateCascaderTransformer } from '@/presentation/components/tds-form/utils';
import { useTdsForm } from '@/presentation/hooks/form';
import { getIssueFilterConfig } from '@/presentation/pages/setting/data-labeling/config';
import { useStores } from '@/presentation/store';
import { CommonFieldKey, FIELD_LABEL, FieldKey, StandardCommonFieldKey, Zone } from '@/proto/field';
import { IssueType } from '@/proto/issue';
import { IFilter } from '@/proto/metric';
import { genStandardCompareToken, genStandardToken, KeyDataType } from '@/proto/token';
import { IOSAnrListContext } from '../context';

interface IProps {
  entry: FilterEntry;
  onTokenChange?: (token: string) => void;
}

export const ProblemForm: FC<IProps> = (props) => {
  const config = useAppConfig();
  const formRef = useRef<ProFormInstance>();
  const compareFormRef = useRef<ProFormInstance>();
  const [, useTdsFormContext] = useTdsForm;
  const { formContext, updateFormContext } = useTdsFormContext();
  const [showSwitch, setShowSwitch] = useState(false);
  const splitRef = useRef<any>();
  const { setSharedFields, setSharedCompareFields } = useContext(IOSAnrListContext);

  const [,setState] = useUrlState({
    token: '',
  });
  const [timeRange, setTimeRange] = useLocalStorageState(
    'ios-anr-list-v1',
    {
      defaultValue: DEFAULT_EVENT_TIME_RANGE,
    },
  );

  const { filterConfig } = useStores().ProductStore;
  const inferredLabelsConfig = getIssueFilterConfig(IssueType.ISSUE_TYPE_ANR, filterConfig);

  const onSplitAppVersion = (value?: string[]) => {
    const appversion: CharacterInputValue = formRef.current?.getFieldValue(StandardCommonFieldKey.AppVersion);

    if ([MatchMode.Between, MatchMode.NOTEMPTY, MatchMode.ISEMPTY].includes(appversion?.mode as MatchMode)) {
      message.warning('请在多选的匹配模式下使用');
      return;
    }

    formRef.current?.setFieldsValue({
      [StandardCommonFieldKey.AppVersion]: {
        ...appversion,
        values: value || [],
      },
    });
  };

  const showModal = () => {
    const appversion: CharacterInputValue = formRef.current?.getFieldValue(StandardCommonFieldKey.AppVersion);
    splitRef.current?.showModal(appversion?.values?.join('\n'));
  };

  const formItems: TdsFormItem[] = [
    {
      type: TdsFormItemType.DateCascader,
      label: '时间范围',
      name: StandardCommonFieldKey.TimeRange,
      width: 462,
      deleteable: false,
      convertValue: dateCascaderConverter,
      transform: dateCascaderTransformer,
      fieldProps: {
        maxLimitedDays: 90,
        timeWindowOptions: ISSUE_TIME_TYPE,
        timeTypeOptions: EVENT_TIME_OPTIONS,
      },
      inFilter: true,
      filterType: TdsFilterType.DateCascader,
    },
    {
      type: TdsFormItemType.CharacterInput,
      width: 360,
      label: <SplitModal ref={splitRef} callBack={onSplitAppVersion} onTrigger={showModal} trigger={hintChildren(
        (
        <Space size={4}>
          <span>{FIELD_LABEL[FieldKey.AppVersion]}</span>
          <span className={'underline cursor-pointer flex align-center'}><StringTooltipIcon /></span>
        </Space>
        ), (
          <div>
            <div>{versionFilterTips}</div>
            <div onClick={showModal} className={'underline cursor-pointer'}>点击icon进入批量输入编辑页</div>
          </div>
        ),
      )} />,
      labelString: 'APP版本',
      name: StandardCommonFieldKey.AppVersion,
      fieldProps: {
        mode: 'tags',
        valueSelectProps: {
          fieldKey: CommonFieldKey.AppVersion,
          fieldScope: Zone.Anr,
        },
        modeOptions: VERSION_MATCH_OPTIONS,
      },
      inFilter: true,
      filterName: CommonFieldKey.AppVersion,
      filterType: TdsFilterType.Version,
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: '场景',
      name: StandardCommonFieldKey.Stage,
      inFilter: true,
      filterType: TdsFilterType.String,
      filterName: CommonFieldKey.Stage,
      fieldProps: {
        mode: 'tags',
        valueSelectProps: {
          fieldKey: CommonFieldKey.Stage,
          fieldScope: Zone.Anr,
        },
        modeOptions: CHARACTER_MATCH_OPTIONS,
      },
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: '系统版本',
      name: StandardCommonFieldKey.OsVersion,
      fieldProps: {
        mode: 'tags',
        valueSelectProps: {
          fieldKey: CommonFieldKey.OsVersion,
          fieldScope: Zone.Anr,
        },
        modeOptions: VERSION_MATCH_OPTIONS,
      },
      inFilter: true,
      filterName: CommonFieldKey.OsVersion,
      filterType: TdsFilterType.Version,
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: 'SDK版本',
      name: StandardCommonFieldKey.SdkVersion,
      fieldProps: {
        mode: 'tags',
        valueSelectProps: {
          fieldKey: CommonFieldKey.SdkVersion,
          fieldScope: Zone.Anr,
        },
        modeOptions: VERSION_MATCH_OPTIONS,
      },
      inFilter: true,
      filterName: CommonFieldKey.SdkVersion,
      filterType: TdsFilterType.Version,
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: '机型',
      name: StandardCommonFieldKey.Model,
      inFilter: true,
      filterType: TdsFilterType.String,
      filterName: CommonFieldKey.Model,
      fieldProps: {
        mode: 'tags',
        valueSelectProps: {
          fieldKey: CommonFieldKey.Model,
          fieldScope: Zone.Anr,
        },
        modeOptions: CHARACTER_MATCH_OPTIONS,
      },
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: '用户ID',
      name: StandardCommonFieldKey.AccountId,
      inFilter: true,
      filterType: TdsFilterType.String,
      filterName: CommonFieldKey.AccountId,
      fieldProps: {
        mode: 'tags',
        valueSelectProps: {
          fieldKey: CommonFieldKey.AccountId,
          fieldScope: Zone.Anr,
        },
        modeOptions: CHARACTER_MATCH_OPTIONS,
      },
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: '设备ID',
      name: StandardCommonFieldKey.Qimei,
      inFilter: true,
      filterType: TdsFilterType.String,
      filterName: CommonFieldKey.Qimei,
      fieldProps: {
        mode: 'tags',
        valueSelectProps: {
          fieldKey: CommonFieldKey.Qimei,
          fieldScope: Zone.Anr,
        },
        modeOptions: CHARACTER_MATCH_OPTIONS,
      },
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: hintText('启动ID', launchIdText),
      labelString: '启动ID',
      name: StandardCommonFieldKey.LaunchId,
      inFilter: true,
      filterType: TdsFilterType.String,
      filterName: CommonFieldKey.LaunchId,
      fieldProps: {
        mode: 'tags',
        valueSelectProps: {
          fieldKey: CommonFieldKey.LaunchId,
          fieldScope: Zone.Anr,
        },
        modeOptions: CHARACTER_MATCH_OPTIONS,
      },
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: '消息ID',
      name: StandardCommonFieldKey.ClientIdentify,
      inFilter: true,
      filterType: TdsFilterType.String,
      filterName: CommonFieldKey.ClientIdentify,
      fieldProps: {
        mode: 'tags',
        valueSelectProps: {
          fieldKey: CommonFieldKey.ClientIdentify,
          fieldScope: Zone.Anr,
        },
        modeOptions: CHARACTER_MATCH_OPTIONS,
      },
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: 'Issue ID',
      name: StandardCommonFieldKey.Feature,
      inFilter: true,
      filterType: TdsFilterType.String,
      filterName: CommonFieldKey.Feature,
      fieldProps: {
        mode: 'tags',
        valueSelectProps: {
          fieldKey: CommonFieldKey.Feature,
          fieldScope: Zone.Anr,
        },
        modeOptions: CHARACTER_MATCH_OPTIONS,
      },
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: hintText('问题特征', stringFilterTips),
      labelString: '问题特征',
      name: StandardCommonFieldKey.FuncName,
      inFilter: true,
      filterType: TdsFilterType.String,
      filterName: CommonFieldKey.FuncName,
      fieldProps: {
        mode: 'tags',
        modeOptions: CHARACTER_MATCH_OPTIONS,
      },
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: hintText('出错堆栈', stringFilterTips),
      labelString: '出错堆栈',
      name: StandardCommonFieldKey.Stack,
      inFilter: true,
      filterType: TdsFilterType.String,
      filterName: CommonFieldKey.TranslatedStacks,
      fieldProps: {
        mode: 'tags',
        modeOptions: STACK_CHARACTER_MATCH_OPTIONS,
      },
      convertValue: tokenizeValue,
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: hintText('Bundle ID', BUNDLE_TIPS_CASE),
      labelString: 'Bundle ID',
      name: StandardCommonFieldKey.BundleId,
      inFilter: true,
      filterType: TdsFilterType.String,
      filterName: CommonFieldKey.BundleId,
      fieldProps: {
        mode: 'tags',
        valueSelectProps: {
          fieldKey: CommonFieldKey.BundleId,
          fieldScope: Zone.Anr,
        },
        modeOptions: CHARACTER_MATCH_OPTIONS,
      },
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: '实验ID',
      name: StandardCommonFieldKey.ShiplyAbTestTag,
      filterName: CommonFieldKey.ShiplyAbTestTag,
      inFilter: true,
      filterType: TdsFilterType.Custom,
      filterTransform: (_fieldKey, fieldValue) => customFields2Filter(
        CommonFieldKey.ShiplyAbTestTag,
        fieldValue,
        'a11',
      ),
      fieldProps: {
        valueSelectProps: {
          fieldKey: CommonFieldKey.ShiplyTag,
          fieldScope: Zone.Anr,
        },
        mode: 'tags',
        modeOptions: CHARACTER_MATCH_ARRAY_OPTIONS,
      },
      hidden: !config.enableShiplyAbTestTag,
    },
    {
      type: TdsFormItemType.CharacterInput,
      hidden: !config.enableAbTestTag,
      label: '业务下钻',
      name: StandardCommonFieldKey.AbTestTag,
      filterName: CommonFieldKey.ShiplyAbTestTag,
      inFilter: true,
      filterType: TdsFilterType.Custom,
      filterTransform: (_fieldKey, fieldValue) => customFields2Filter(
        CommonFieldKey.ShiplyAbTestTag,
        fieldValue,
        'a12',
      ),
      fieldProps: {
        valueSelectProps: {
          fieldKey: CommonFieldKey.AbTestTag,
          fieldScope: Zone.Anr,
        },
        mode: 'tags',
        modeOptions: CHARACTER_MATCH_ARRAY_OPTIONS,
      },
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: '个例标签',
      name: StandardCommonFieldKey.TagsInFilter,
      filterName: CommonFieldKey.TagsInFilter,
      inFilter: true,
      filterType: TdsFilterType.Custom,
      filterTransform: (_fieldKey, fieldValue) => customFields2Filter(
        CommonFieldKey.TagsInFilter,
        fieldValue,
        'a13',
      ),
      fieldProps: {
        valueSelectProps: {
          fieldKey: CommonFieldKey.TagsInFilter,
          fieldScope: Zone.Anr,
        },
        mode: 'tags',
        modeOptions: CHARACTER_MATCH_ARRAY_OPTIONS,
      },
      hideInCompare: true,
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: hintText('数据标记', DATA_LABELING_TIPS),
      labelString: '数据标记',
      name: StandardCommonFieldKey.StandardInferredLabels,
      filterName: CommonFieldKey.StandardInferredLabels,
      inFilter: true,
      filterType: TdsFilterType.Custom,
      initialValue: {
        mode: MatchMode.NOT,
        values: inferredLabelsConfig?.values?.map(item => item.value),
      },
      filterTransform: (_fieldKey, fieldValue) => customFields2Filter(
        CommonFieldKey.StandardInferredLabels,
        fieldValue,
      ),
      fieldProps: {
        valueSelectProps: {
          options: inferredLabelsConfig?.labelOptions || [],
        },
        mode: 'tags',
        modeOptions: INFERRED_LABELS_OPTIONS,
      },
      hideInCompare: true,
      hidden: !isDataLabelingPlatform(),
    },
    {
      type: TdsFormItemType.FieldSelector,
      name: 'showNew',
      label: '只显示新增问题',
      hidden: props.entry !== FilterEntry.ANRIOSList || !showSwitch,
      deleteable: false,
      fieldProps: {
        mode: 'tags',
        valueSelectProps: {
          fieldKey: FieldKey.ShowNew,
          fieldScope: Zone.Anr,
        },
        modeOptions: CHARACTER_MATCH_OPTIONS,
      },
      hideInCompare: true,
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: hintText('构建号', ABTEST_TIPS),
      labelString: '构建号',
      name: StandardCommonFieldKey.BuildNumber,
      inFilter: true,
      filterType: TdsFilterType.String,
      filterName: CommonFieldKey.BuildNumber,
      fieldProps: {
        mode: 'tags',
        valueSelectProps: {
          fieldKey: CommonFieldKey.BuildNumber,
          fieldScope: Zone.Anr,
        },
        modeOptions: CHARACTER_MATCH_OPTIONS,
      },
    },
    {
      type: TdsFormItemType.NumberInput,
      label: hintText('使用时长（秒）', numberFilterTips),
      labelString: '使用时长（秒）',
      name: StandardCommonFieldKey.StandardLaunchDuration,
      fieldProps: {
        modeOptions: NUMBER_MATCH_OPTIONS,
      },
      inFilter: true,
      filterName: CommonFieldKey.LaunchDuration,
      filterType: TdsFilterType.Number,
    },
    {
      type: TdsFormItemType.CustomPanel,
      label: '自定义维度',
      name: StandardCommonFieldKey.UserCustom,
      inFilter: true,
      filterType: TdsFilterType.Custom,
      filterName: CommonFieldKey.UserCustom,
      fieldProps: {
        issueType: IssueType.ISSUE_TYPE_ANR,
        type: 'customFields',
      },
      filterTransform: (_fieldKey: string, fieldValue: TdsCustomFieldsValue[]) => fieldValue?.map((item: any) => ({
        name: CommonFieldKey.UserCustom,
        operator: item.operator,
        values: item.values || [],
        key: item.key,
        type: KeyDataType.STRING,
      })) ?? [],
    },
    {
      type: TdsFormItemType.CustomPanel,
      label: '自定义字段',
      name: StandardCommonFieldKey.BizExtendInfo,
      inFilter: true,
      filterType: TdsFilterType.Custom,
      filterName: CommonFieldKey.BizExtendInfo,
      fieldProps: {
        issueType: IssueType.ISSUE_TYPE_ANR,
        type: 'customKey',
      },
      filterTransform: (_fieldKey: string, fieldValue: TdsCustomFieldsValue[]) => fieldValue?.map((item: any) => ({
        name: CommonFieldKey.BizExtendInfo,
        operator: item.operator,
        values: item.values || [],
        key: item.key,
        type: KeyDataType.STRING,
      })) ?? [],
    },
  ];

  const onFieldsChange = (fields: any) => {
    if (fields?.[0]?.name?.includes(StandardCommonFieldKey.AppVersion)) {
      setShowSwitch(fields?.[0]?.value?.mode === MatchMode.OR && !isEmpty(fields?.[0]?.value?.values));
    }
  };

  const onCompareFinish = async (values: CompareParams) => {
    const token = await genStandardCompareToken(values);

    updateFormContext(draft => ({
      ...draft,
      token,
      fieldsValue: values.fieldsValue,
      fieldsFilter: values.fieldsFilters,
      compareFieldsValue: values.compareFieldsValue,
      compareFieldsFilter: values.compareFieldsFilters,
      uuidFetch: uuid.v4(),
    }));

    setState({
      token,
    });
  };

  const onFinish = async (values: Record<string, any>, filters: IFilter[]) => {
    const formatValues = {
      ...values,
      timeWindow: values?.time_window_type,
    };
    // 缓存选择过的时间范围
    setTimeRange(values?.[StandardCommonFieldKey.TimeRange] ?? DEFAULT_EVENT_TIME_RANGE);
    const token = await genStandardToken({
      ...formatValues,
      filters,
    });

    const genUuid = uuid.v4();
    updateFormContext(draft => ({
      ...draft,
      token,
      fieldsValue: values,
      fieldsFilter: filters,
      uuidFetch: genUuid,
    }));

    setState({
      token,
    });
    props.onTokenChange?.(token);
  };

  const enableCompare = props.entry === FilterEntry.ANRIOSList;

  return (
    <TdsForm
      onFieldsChange={onFieldsChange}
      onCompareFinish={onCompareFinish}
      enableCompare={props.entry === FilterEntry.ANRIOSList}
      items={formItems}
      compareFormRef={compareFormRef}
      formRef={formRef}
      name={'anr-analytics'}
      pageId={PAGE_ID.anr.analytics}
      onFinish={onFinish}
      compareOpen={enableCompare ? formContext.isCompare : false}
      tokenControl={{
        refreshToken: true,
        onParsed: (values: any) => {
          setSharedFields?.(values.fields);
          setSharedCompareFields?.(values.fieldsCompare);
        },
      }}
      initialValues={{
        [StandardCommonFieldKey.TimeRange]: timeRange,
      }}
    />
  );
};

